/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { useCallback, type FC } from 'react';
import HTMLReactParser from 'html-react-parser';
import type { SalaryContinuanceCalcProps, SalaryContinuanceItemResolver, SalaryContinuanceQuery, useSalaryContinuanceData } from '../types';
import Image from '2-core-elements/Image/Image';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCalculatorQuery, useSalaryContinuanceCalc, resolverSalaryContinuance, displayFormat } from '../calculatorLibs';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import SimpleStatisticCard from '3-components/SimpleStatisticCard/SimpleStatisticCard';
import SalaryContinuanceCalcForm from './SalaryContinuanceCalcForm';

const SalaryContinuanceCalc: FC<SalaryContinuanceCalcProps> = ({ label, table, dataUrl }) => {
    const {
        titleText,
        subtitleText,
        infoText,
        labelAge,
        labelGender,
        labelSalary,
        labelSalaryInsured,
        maxInsuredPercentage,
        defaultUnit = 100,
        imageDesktopUrl,
        calculateText,
    } = label;

    const {
        tableTitle,
        insuredMonthlyText,
        insuredMonthlyDescription,
        insuredAnnualText,
        insuredAnnualDescription,
        rowTitleText,
        row30dText,
        row60dText,
        row90dText,
        column2yText,
        column5yText,
        column60yText,
        column65yText,
    } = table;

    const { calculatorData } = dataUrl;

    const { data, isLoading, isError, error } = useCalculatorQuery(calculatorData, 'salary-continuance-query', {
        queryKey: ['salary-continuance-calculator'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: SalaryContinuanceQuery) => {
            return resolverSalaryContinuance(result);
        }, []),
    });

    const { setData, setDefaultUnit, annualBenefit, monthlyBenefit, result } = useSalaryContinuanceCalc();

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={`Loading data`} />
        );
    }

    if (isError || !data) {
        console.error(error);
        return (
            <ListingError errorMessage={`Error to initialize component`} />
        );
    }

    const ageOptions = data.salaryContinuance2year.map(item => {
        return {
            label: item.age.toString(),
            value: item.age.toString(),
        };
    });

    return (
        <div className="calculator-app">
            <div className="container">
                <div className="section-header">
                    <div className="section-heading">
                        <h2>{titleText}</h2>
                    </div>
                    <div className="section-description">
                        {HTMLReactParser(subtitleText)}
                    </div>
                </div>
                <div className="calculator-app-wrapper">
                    <div className="calc-wrapper">
                        <div className="row-calc">
                            <div className="col-calc">
                                <SalaryContinuanceCalcForm
                                    ageOptions={ageOptions}
                                    labels={{
                                        ageLabel: labelAge,
                                        genderLabel: labelGender,
                                        salaryLabel: labelSalary,
                                        percentageLabel: labelSalaryInsured,
                                        calculateLabel: calculateText,
                                    }}
                                    maxInsuredPercentage={maxInsuredPercentage}
                                    onSubmit={(formResult): void => {
                                        const { age, gender, salary, percentageInsuredSalary } = formResult;

                                        const continuanceData: useSalaryContinuanceData = {
                                            salary,
                                            gender,
                                            percentageInsuredSalary,
                                        };

                                        const periods = [
                                            `2year`,
                                            `5year`,
                                            `60year`,
                                            `65year`
                                        ];

                                        periods.forEach(period => {
                                            const feePeriod = data[`salaryContinuance${period}`].find((row: SalaryContinuanceItemResolver) => {
                                                return row.age === age;
                                            });

                                            if (feePeriod) {
                                                continuanceData[`fee${period}Period`] = { ...feePeriod };
                                            }
                                        });

                                        setDefaultUnit(defaultUnit);
                                        setData(continuanceData);
                                    }}
                                />
                            </div>
                            <div className="col-calc">
                                {imageDesktopUrl && (
                                    <div className="image-calc-wrapper">
                                        <Image
                                            imgSrc={imageDesktopUrl}
                                            imgCustomClass="image-calc"
                                            imgAlt="salary-continuance-image"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {annualBenefit && monthlyBenefit ? (
                        <div className="calculator-result-wrapper">
                            <div className="calc-text-content">
                                <h3>{tableTitle}</h3>
                            </div>

                            <div className="calculator-result-grid">
                                <div className="calculator-result-col">
                                    <SimpleStatisticCard
                                        cardTitle={displayFormat(annualBenefit)}
                                        cardSubtitle={insuredAnnualText}
                                        cardDescription={insuredAnnualDescription}
                                        className="calculator-result-card"
                                    />
                                </div>
                                <div className="calculator-result-col">
                                    <SimpleStatisticCard
                                        cardTitle={displayFormat(monthlyBenefit)}
                                        cardSubtitle={insuredMonthlyText}
                                        cardDescription={insuredMonthlyDescription}
                                        className="calculator-result-card"
                                    />
                                </div>
                            </div>

                            <div className="calculator-result-table">
                                <table className="salary-continuance-table">
                                    <thead>
                                        <tr>
                                            <th>{rowTitleText}</th>
                                            <th>{column2yText}</th>
                                            <th>{column5yText}</th>
                                            <th>{column60yText}</th>
                                            <th>{column65yText}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{row30dText}</td>
                                            <td>{displayFormat(result.fee2yearPeriod.fee30D)}</td>
                                            <td>{displayFormat(result.fee5yearPeriod.fee30D)}</td>
                                            <td>{displayFormat(result.fee60yearPeriod.fee30D)}</td>
                                            <td>{displayFormat(result.fee65yearPeriod.fee30D)}</td>
                                        </tr>
                                        <tr>
                                            <td>{row60dText}</td>
                                            <td>{displayFormat(result.fee2yearPeriod.fee60D)}</td>
                                            <td>{displayFormat(result.fee5yearPeriod.fee60D)}</td>
                                            <td>{displayFormat(result.fee60yearPeriod.fee60D)}</td>
                                            <td>{displayFormat(result.fee65yearPeriod.fee60D)}</td>
                                        </tr>
                                        <tr>
                                            <td>{row90dText}</td>
                                            <td>{displayFormat(result.fee2yearPeriod.fee90D)}</td>
                                            <td>{displayFormat(result.fee5yearPeriod.fee90D)}</td>
                                            <td>{displayFormat(result.fee60yearPeriod.fee90D)}</td>
                                            <td>{displayFormat(result.fee65yearPeriod.fee90D)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="calc-text-content">{HTMLReactParser(infoText)}</div>
                        </div>
                    ) : ''}
                </div>
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface SalaryContinuanceInitProps {
    el: HTMLDivElement;
    props: SalaryContinuanceCalcProps;
}

const SalaryContinuanceCalcInit = ({ el, props }: SalaryContinuanceInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <SalaryContinuanceCalc {...props} />
        </QueryClientProvider>
    );
};

export default SalaryContinuanceCalcInit;
